import type { Placement } from '@popperjs/core';
import type { ButtonSize } from '@timelyapp/react';
import { Text, Tooltip, useDebouncedEffect } from '@timelyapp/react';
import classNames from 'classnames';
import Label from 'components/Label';
import Spinner from 'components/Spinner';
import config from 'config';
import type { ProjectDropdownOption } from 'factories/projects';
import { createClientProjectTree, incompleteOption, normalizeProject } from 'factories/projects';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { EnrichedForecast } from 'routes/forecast/types/forecast';
import type { Client } from 'types/client';
import type { EnrichedProject, ProjectBase } from 'types/project';
import { getProjectColor } from 'utils/project';
import type { RenderOption, RowNode } from './DropdownBuilder';
import DropdownBuilder from './DropdownBuilder';
import DiagnosticResourceId from './dev-tools/DiagnosticResourceId';
import ProgressBar from './ProgressBar';
import SelectOption from './ProjectSelect/SelectOption';
import styles from './ProjectSelect.module.scss';
import ProjectBadge from './projects/ProjectBadge';

export const PROJECT_SELECT_TEST_ID = 'dropdown_project_select';

export interface ProjectSelectProps {
  name?: string;
  disabled: boolean;
  value: number | null;
  projects: Array<EnrichedProject>;
  recentProjects: Array<EnrichedProject>;
  pinnedProjects?: Array<EnrichedProject>;
  forecasts?: Array<EnrichedForecast>;
  /** Do we have a forecast selected? */
  forecastId?: number | null;
  clients: Array<Client>;
  className?: string;
  inputClassName?: string;
  overlayClassName?: string;
  onChange: (project: ProjectBase, forecastId?: number) => void;
  onCreateNew?: (item: { name: string }) => void;
  onOpenChange?: (value: boolean) => void;
  allowCreate?: boolean;
  currencySymbol: string;
  isLoading?: boolean;
  placeholder: string; // defaultProp'd,
  placeholderColor: string; // defaultProp'd
  toggleOpen?: boolean;
  onSearch: (filterString: string) => Promise<unknown>;
  /** Optional node rendered pinned below the dropdown list (e.g. a "Show all projects" toggle). */
  listFooter?: React.ReactNode;
  allProjectsLoaded?: boolean;
  inputCaret?: boolean;
  hideColorBox?: boolean;
  popoverFixed?: boolean;
  maxHeight?: number;
  colorBoxIcon?: React.ReactNode;
  loadingIndicatorClassName?: string;
  size?: ButtonSize;
  /// Render popover in portal
  usePortal?: boolean;
  /** Placement of the dropdown popover (defaults to bottom-start). */
  popoverPosition?: Placement;
  /** popper.js offset `[skidding, distance]` for the dropdown popover (defaults to `[0, 4]`). */
  popoverOffset?: [number, number];
  /** Display client name on a separate line below the project name instead of inline */
  multiLinePreview?: boolean;
  /** Apply button-style borders and styling */
  asButton?: boolean;
  /** Show archived projects in the dropdown (default: false) */
  showArchivedProjects?: boolean;
}

const ProjectSelect = ({
  allowCreate,
  allProjectsLoaded,
  asButton,
  className,
  clients,
  colorBoxIcon,
  currencySymbol,
  disabled,
  forecastId,
  forecasts = [],
  hideColorBox,
  inputCaret,
  inputClassName,
  isLoading,
  listFooter,
  loadingIndicatorClassName,
  maxHeight,
  multiLinePreview,
  onChange,
  onCreateNew,
  onOpenChange,
  onSearch,
  overlayClassName,
  placeholder,
  placeholderColor,
  popoverFixed,
  popoverPosition,
  popoverOffset,
  projects,
  recentProjects,
  pinnedProjects = [],
  showArchivedProjects = false,
  size = 'small',
  toggleOpen,
  value,
  usePortal,
}: ProjectSelectProps) => {
  const [hasFocus, setHasFocus] = useState(false);
  const [isOpen, setIsOpenInState] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [filterString, onSetFilterString] = useState('');
  // `onSearch` is a dependency, so a consumer that swaps the handler (e.g. the
  // hour editor switching between member-only and all-projects search) re-runs
  // the search for the current query without any extra prop.
  useDebouncedEffect(() => onSearch(filterString), [filterString, onSearch], 300);

  const setIsOpen = useCallback(
    (o: boolean) => {
      setIsOpenInState(o);
      if (onOpenChange) {
        onOpenChange(o);
      }
    },
    [setIsOpenInState, onOpenChange]
  );

  const activeForecast = useMemo(
    () => forecasts.find(f => f.id === forecastId),
    [forecasts, forecastId]
  );

  // NOTE: We can use the description to display an identifier in the task's tooltip
  const forecastDescriptionById = useMemo(() => {
    const map = new Map<number, string>();
    for (const forecast of forecasts) {
      const description = (forecast.description ?? '').trim();
      if (description) {
        map.set(forecast.id, description);
      }
    }
    return map;
  }, [forecasts]);

  const handleSelectChange = (v: number | null, item: ProjectDropdownOption | null) => {
    if (item?.createTrigger) {
      if (onCreateNew) {
        onCreateNew({
          name: item.createValue || '',
        });
      }
    } else if (item?.project) {
      onChange(item.project, item.forecastId);
    }
    setHasFocus(false);
    setIsOpen(false);
  };

  function renderLoadingIndicator() {
    if (!isLoading) {
      return null;
    }

    return (
      <div className={classNames(styles.loadingIndicator, loadingIndicatorClassName)}>
        <Spinner length={6} />
      </div>
    );
  }

  React.useEffect(() => {
    if (toggleOpen && !isOpen) {
      setIsOpen(true);
      setHasFocus(true);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [toggleOpen, isOpen, setIsOpen]);

  const getOptions: () => Array<ProjectDropdownOption> = useMemo(() => {
    let options: Array<ProjectDropdownOption> | null = null;
    return () =>
      (options ??= normalizeData({
        projects,
        pinnedProjects,
        recentProjects,
        forecasts,
        clients,
        currencySymbol,
        showArchivedProjects,
      }));
  }, [
    projects,
    pinnedProjects,
    recentProjects,
    clients,
    forecasts,
    currencySymbol,
    showArchivedProjects,
  ]);

  const postProcessOptions = useCallback(
    (
      filteredRowNodes: Array<RowNode<number, ProjectDropdownOption>>,
      filterString: string,
      pushOption: (normalizedProject: ProjectDropdownOption) => void
    ) => {
      if (!allProjectsLoaded && filterString.length < 1) {
        pushOption(incompleteOption);
      }
      if (allowCreate) {
        const isOnlyOption = !filteredRowNodes.length;
        if (filterString.length > 0) {
          // All filtered out
          pushOption(createTriggerFactory(filterString, isOnlyOption));
        } else {
          pushOption(createTriggerFactory(null, isOnlyOption));
        }
      }
    },
    [allowCreate, allProjectsLoaded]
  );

  const containerClass = classNames(styles.container, className, {
    [styles.asButton]: asButton,
    [styles.disabled]: asButton && disabled,
  });
  const inputRowClass = classNames(styles.inputRow, inputClassName);

  // Check if the project exists in the projects list
  let foundProject = null;
  if (value != null) {
    foundProject = projects.find(p => {
      if (activeForecast) return p.base.id === value && activeForecast?.project?.id === value;
      return p.base.id === value;
    });
  }

  // Combine project+forecast ids into a unique value, to differentiate between "just project"
  // See normalizeData util for why we're doing this
  // If project not found in list, treat as null to avoid selecting wrong item
  const valueCombination = foundProject
    ? forecastId
      ? Number(`${value}${forecastId}`)
      : value
    : null;

  let boxColor = config.suggestionDefaultProjectColor;
  let selectedProjectTic = activeForecast?.tic || activeForecast?.project?.tic;
  let placeholderElement = (
    <SelectedValue
      name={placeholder}
      disabled={disabled}
      forecast={activeForecast}
      size={size}
      multiLinePreview={multiLinePreview}
    />
  );

  if (foundProject != null) {
    boxColor = getProjectColor(foundProject.base);
    selectedProjectTic = activeForecast?.project?.tic ?? foundProject.base.tic;
    placeholderElement = (
      <SelectedValue
        forecast={activeForecast}
        name={foundProject.base.name}
        project={foundProject.base}
        disabled={disabled}
        client={foundProject.base.client.name}
        size={size}
        multiLinePreview={multiLinePreview}
      />
    );
  }

  return (
    <DropdownBuilder
      value={valueCombination}
      options={getOptions}
      onSetIsOpen={setIsOpen}
      onFocusChanged={setHasFocus}
      onChange={handleSelectChange}
      disabled={disabled}
      optionHeight={24}
      renderOption={renderOption}
      getOptionTitle={option => {
        // Only apply to forecast “Tasks” rows (leaf options with forecastId)
        if (!option?.forecastId) {
          return option?.label;
        }
        const description = (forecastDescriptionById.get(option.forecastId) ?? '').trim();
        return description ? `${option.label} (${description})` : option.label;
      }}
      matchOption={matchProjectOption}
      postProcessOptions={postProcessOptions}
      placeholder={placeholder}
      isOpen={isOpen}
      filterString={filterString}
      onSetFilterString={onSetFilterString}
      inputCaret={inputCaret}
      popoverFixed={!!popoverFixed}
      maxHeight={maxHeight}
      inlineInput
      inputRef={inputRef}
    >
      {context => (
        <div className={containerClass}>
          {renderLoadingIndicator()}
          <div className={inputRowClass}>
            {!hideColorBox &&
              (colorBoxIcon ?? (
                <div
                  className={styles.colorBoxWrap}
                  onClick={() => {
                    if (!disabled) {
                      setIsOpen(true);
                      setHasFocus(true);
                      setTimeout(() => {
                        inputRef.current?.focus();
                      }, 50);
                    }
                  }}
                >
                  <ProjectBadge
                    color={`#${boxColor || config.suggestionDefaultProjectColor}`}
                    icon={activeForecast ? 'task' : undefined}
                    size="small"
                    ticMeta={selectedProjectTic}
                  />
                </div>
              ))}
            <div className={styles.inputWrap}>
              {context.renderInput({
                variant: 'asText',
                className: classNames(styles.input, styles[`size-${size}`]),
                onMouseDownCapture: e => {
                  if (isOpen) {
                    const event = e.nativeEvent;
                    event.stopImmediatePropagation();
                    event.preventDefault();
                    setTimeout(() => {
                      if (event.target instanceof HTMLElement) {
                        event.target.blur();
                      }
                    }, 100);
                  }
                },
                style: {
                  opacity: hasFocus || isOpen ? 1 : 0,
                },
                'data-testid': PROJECT_SELECT_TEST_ID,
              })}
              <div
                className={classNames(styles.overlay, overlayClassName)}
                style={{
                  opacity: hasFocus || isOpen ? 0 : 1,
                  color: value == null ? placeholderColor : undefined,
                }}
              >
                {placeholderElement}
              </div>
            </div>
          </div>
          {context.isOpen &&
            context.renderListPopover({
              renderListProps: { width: 380 },
              usePortal,
              popoverPosition,
              popoverOffset,
              popoverListFooter: listFooter,
            })}
        </div>
      )}
    </DropdownBuilder>
  );
};

ProjectSelect.defaultProps = {
  placeholder: 'Select a Project',
  placeholderColor: '#aaa',
  recentProjects: [],
  pinnedProjects: [],
  disabled: false,
  inputCaret: true,
  options: [],
};

export default ProjectSelect;

function normalizeData({
  projects,
  pinnedProjects,
  clients,
  currencySymbol,
  recentProjects,
  forecasts,
  showArchivedProjects,
}: {
  projects: Array<EnrichedProject>;
  pinnedProjects: Array<EnrichedProject>;
  recentProjects: Array<EnrichedProject>;
  clients: Array<Client>;
  forecasts: Array<EnrichedForecast>;
  currencySymbol: string;
  showArchivedProjects?: boolean;
}): Array<ProjectDropdownOption> {
  const options = createClientProjectTree(projects, clients, {
    currencySymbol,
    onlyActive: !showArchivedProjects,
  });

  if (recentProjects && recentProjects.length > 0) {
    const normalizedRecentProjects = recentProjects.map(project => {
      return {
        ...normalizeProject(project),
        showClient: true,
        currency: currencySymbol,
      };
    });

    options.unshift({
      value: -1,
      rowKey: 'recent-header',
      label: 'Recent Projects',
      children: normalizedRecentProjects,
      project: null,
    });
  }

  if (pinnedProjects && pinnedProjects.length > 0) {
    const normalizedPinnedProjects = pinnedProjects.map(project => {
      return {
        ...normalizeProject(project),
        showClient: true,
        currency: currencySymbol,
      };
    });

    options.unshift({
      value: -1,
      rowKey: 'pinned-header',
      label: 'Pinned Projects',
      children: normalizedPinnedProjects,
      project: null,
    });
  }

  if (forecasts && forecasts.length > 0) {
    const normalizedForecastsAsProjects = forecasts.map(forecast => {
      // The list shows project+forecast options alongside project-only options,
      // so we combined the project+forecast id to create unique option value.
      // This does not impact public props API remains unchanged via handleSelectChange
      const valueCombination = Number(`${forecast.project?.id}${forecast.id}`);
      // Use task's TIC metadata if available, otherwise fall back to project's TIC
      const projectWithTic = forecast.project
        ? { ...forecast.project, tic: forecast.tic || forecast.project.tic }
        : undefined;
      return {
        value: valueCombination,
        forecastId: forecast.id,
        label: forecast.parent_title
          ? `[${forecast.parent_title}] ${forecast.title}`
          : forecast.title,
        color: getProjectColor(forecast.project),
        client: `${forecast.project?.name}, ${forecast.project?.client?.name}`,
        clientId: forecast.project?.client?.id,
        project: projectWithTic,
        budgetSpendInPercent: forecast.meta.progressedPercent,
        isArchived: false,
        isPinned: false,
        showClient: true,
        currency: currencySymbol,
      };
    });

    options.unshift({
      value: -1,
      rowKey: 'tasks-header',
      label: 'Tasks',
      children: normalizedForecastsAsProjects,
      project: null,
    });
  }

  return options;
}

const createTriggerFactory = (
  createValue: string | null = null,
  isOnlyOption: boolean = false
): ProjectDropdownOption => {
  const createTrigger: ProjectDropdownOption = {
    value: -1,
    rowKey: 'create-trigger',
    project: null,
    label: 'Create new project',
    createTrigger: true,
    createValue,
  };

  if (!createValue) {
    return createTrigger;
  }

  return {
    ...createTrigger,
    label: isOnlyOption
      ? `Press ENTER to add "${createValue}" as a new project`
      : `Add "${createValue}" as a new project`,
  };
};

interface SelectedValueProps {
  disabled: boolean;
  name: string;
  client?: string;
  project?: ProjectBase;
  forecast?: EnrichedForecast;
  size?: ButtonSize;
  multiLinePreview?: boolean;
}

const SelectedValue = (props: SelectedValueProps) => {
  const { disabled, client, name, forecast, project, multiLinePreview } = props;
  const activeForecast = !!forecast;
  const description = activeForecast ? (forecast?.description ?? '').trim() : '';
  const nameWithDescription = description ? `${name} (${description})` : name;
  return (
    <div
      className={classNames(styles.selectedValue, styles[`size-${props.size ?? 'medium'}`], {
        [styles.selectedValueDisabled]: disabled,
        [styles.isPlanned]: !!activeForecast,
        [styles.multiLine]: multiLinePreview && client,
      })}
    >
      {activeForecast ? (
        <>
          {/* <Tooltip> didn't want to work here, re-visit it later */}
          <div className={styles.progressWrapper}>
            <ProgressBar
              value={forecast?.meta.progressedPercent}
              secondaryValue={forecast?.meta.totalEstimatedProgress}
              size="xxsmall"
            />
          </div>
          <Tooltip
            tooltip={
              <Text as="div" className="fs-mask" size="xsmall">
                {nameWithDescription}
              </Text>
            }
            position="top-start"
            delay={250}
            usePortal
          >
            <span className={classNames('fs-mask', styles.projectOnForecast)}>
              {nameWithDescription}
            </span>
          </Tooltip>
          <small className="fs-mask">
            {forecast?.title} {client ? <>({client})</> : null}
          </small>
        </>
      ) : (
        <>
          {project && <DiagnosticResourceId resource="project" id={project.id} />}
          <Tooltip
            tooltip={
              <Text as="div" className="fs-mask" size="xsmall">
                {name}
              </Text>
            }
            position="top-start"
            delay={250}
            usePortal
          >
            <span className="fs-mask">{name}</span>
          </Tooltip>
          {client ? (
            <small
              className={classNames('fs-mask', { [styles.multiLineClient]: multiLinePreview })}
            >
              {!multiLinePreview && '('}
              {project && <DiagnosticResourceId resource="client" id={project.client.id} />}
              {client}
              {!multiLinePreview && ')'}
            </small>
          ) : null}
          {project && !project.active && (
            <Label size="small" className={styles.archivedLabel}>
              Archived
            </Label>
          )}
        </>
      )}
    </div>
  );
};

const renderOption: RenderOption<ProjectDropdownOption> = (option, idx, meta) => {
  if (option.children) {
    return option.label;
  }
  const optionElement = <SelectOption option={option} isSelected={meta.isSelected} />;
  return option.project?.description ? (
    <Tooltip
      tooltip={
        <Text as="div" className="fs-mask" size="xsmall" ellipsis={6}>
          {option.project.description}
        </Text>
      }
      className={styles.descriptionTooltip}
      position="right"
      delay={250}
      usePortal
    >
      {optionElement}
    </Tooltip>
  ) : (
    optionElement
  );
};

export const matchProjectOption = (option: ProjectDropdownOption, filterString: string) => {
  if (!filterString) {
    return true;
  }
  const searchWords = filterString.toLowerCase().split(' ');

  const clientName = (option.client || '').toLowerCase();
  const projectName = (option.label || '').toLowerCase();

  return searchWords.every(search => clientName.includes(search) || projectName.includes(search));
};
